/**
 * Timezone utilities for notification preferences
 * 
 * Provides a curated list of common IANA timezone identifiers
 * grouped by region for easy selection in the UI.
 */

export interface TimezoneOption {
  value: string; // IANA timezone identifier
  label: string; // Display name
  offset: string; // UTC offset (e.g., "UTC+2")
}

/**
 * Common timezones organized by region
 * Sorted by UTC offset within each region
 */
export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: 'UTC+0' },
  
  // Africa
  { value: 'Africa/Johannesburg', label: 'South Africa (Johannesburg)', offset: 'UTC+2' },
  { value: 'Africa/Cairo', label: 'Egypt (Cairo)', offset: 'UTC+2' },
  { value: 'Africa/Nairobi', label: 'Kenya (Nairobi)', offset: 'UTC+3' },
  { value: 'Africa/Lagos', label: 'Nigeria (Lagos)', offset: 'UTC+1' },
  
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (New York)', offset: 'UTC-5' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)', offset: 'UTC-6' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)', offset: 'UTC-7' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)', offset: 'UTC-8' },
  { value: 'America/Sao_Paulo', label: 'Brazil (São Paulo)', offset: 'UTC-3' },
  { value: 'America/Toronto', label: 'Canada (Toronto)', offset: 'UTC-5' },
  { value: 'America/Mexico_City', label: 'Mexico (Mexico City)', offset: 'UTC-6' },
  
  // Asia
  { value: 'Asia/Dubai', label: 'UAE (Dubai)', offset: 'UTC+4' },
  { value: 'Asia/Kolkata', label: 'India (Kolkata)', offset: 'UTC+5:30' },
  { value: 'Asia/Singapore', label: 'Singapore', offset: 'UTC+8' },
  { value: 'Asia/Tokyo', label: 'Japan (Tokyo)', offset: 'UTC+9' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', offset: 'UTC+8' },
  { value: 'Asia/Shanghai', label: 'China (Shanghai)', offset: 'UTC+8' },
  { value: 'Asia/Seoul', label: 'South Korea (Seoul)', offset: 'UTC+9' },
  { value: 'Asia/Bangkok', label: 'Thailand (Bangkok)', offset: 'UTC+7' },
  
  // Europe
  { value: 'Europe/London', label: 'UK (London)', offset: 'UTC+0' },
  { value: 'Europe/Paris', label: 'France (Paris)', offset: 'UTC+1' },
  { value: 'Europe/Berlin', label: 'Germany (Berlin)', offset: 'UTC+1' },
  { value: 'Europe/Amsterdam', label: 'Netherlands (Amsterdam)', offset: 'UTC+1' },
  { value: 'Europe/Brussels', label: 'Belgium (Brussels)', offset: 'UTC+1' },
  { value: 'Europe/Madrid', label: 'Spain (Madrid)', offset: 'UTC+1' },
  { value: 'Europe/Rome', label: 'Italy (Rome)', offset: 'UTC+1' },
  { value: 'Europe/Athens', label: 'Greece (Athens)', offset: 'UTC+2' },
  { value: 'Europe/Moscow', label: 'Russia (Moscow)', offset: 'UTC+3' },
  
  // Oceania
  { value: 'Australia/Sydney', label: 'Australia (Sydney)', offset: 'UTC+10' },
  { value: 'Australia/Melbourne', label: 'Australia (Melbourne)', offset: 'UTC+10' },
  { value: 'Australia/Perth', label: 'Australia (Perth)', offset: 'UTC+8' },
  { value: 'Pacific/Auckland', label: 'New Zealand (Auckland)', offset: 'UTC+12' },
];

/**
 * Get timezone option by IANA identifier
 */
export const getTimezoneOption = (value: string): TimezoneOption | undefined => {
  return TIMEZONE_OPTIONS.find(tz => tz.value === value);
};

/**
 * Get user's browser timezone (best guess)
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Find closest matching timezone option for user's browser timezone
 * Falls back to UTC if no match found
 */
export const getDefaultTimezoneOption = (): TimezoneOption => {
  const userTz = getUserTimezone();
  const match = getTimezoneOption(userTz);
  
  if (match) {
    return match;
  }
  
  // Fallback to UTC
  return TIMEZONE_OPTIONS[0];
};
